import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

// Mui
import { Box, Button, Chip, InputAdornment, TextField, Typography } from "@mui/material";
import CustomIcon from "components/CustomIcon";

// Icons
import icons from "enums/icons";

// Components
import { CardMessage } from "components/Message";

// Types
import { Team } from "services/user_management/user_management.types";
import { TConvertedUser } from "pages/UserManagement/adaptares/converter";

const SelectTeam = ({
    list,
    users,
    isMultipleUsers = true,
    cancel,
    confirm,
}: {
    list: Team[];
    users: TConvertedUser | TConvertedUser[];
    isMultipleUsers?: boolean;
    cancel: () => void;
    confirm: (selectedUserIds: number[], newTeams: string[]) => Promise<void>;
}) => {
    const intl = useIntl();
    const [filter, setFilter] = useState("");
    const [teams, setTeams] = useState<typeof list & { selected?: boolean }>(list);

    const usersArray = Array.isArray(users) ? users : [users];
    const isFiltering = Boolean(filter.trim().length > 1);
    const isNewTeamName = !Boolean(teams.find((team) => team.name.toLowerCase() === filter.toLowerCase()));
    const filteredList = teams.filter((el) => el.name.toLowerCase().includes(filter.toLowerCase()));

    const selectedUsers = usersArray.filter((user) => user.selectedForTeam);
    const selectedTeams = teams.filter((team) => team.selected).map((team) => team.name);

    const isNameLikePlaceholder = filter.trim().toLowerCase() === "all (default)";
    const isConfirmDisabled = filter.trim() === "" && selectedTeams.length === 0;

    const confirmAddTeam = async () => {
        const userIds = selectedUsers.map((user) => user.id);
        const teamsNames = selectedTeams;

        await confirm(userIds, teamsNames);
        cancel();
    };

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFilter(event.target.value);
    };

    const handleSelectTeam = (chip: Team) => {
        setTeams((prevTeams) =>
            prevTeams.map((el) => ({ ...el, selected: el?.id === chip?.id ? !Boolean(el.selected) : el?.selected }))
        );
    };

    const handleAddTeam = () => {
        setTeams((prevTeams) => [...prevTeams, { id: filter, name: filter, selected: true }]);
        setFilter("");
    };

    return (
        <CardMessage
            hideHeaderBg
            color="primary"
            closeButtonAction={cancel}
            sx={{ maxWidth: "580px !important" }}
            title={
                isMultipleUsers && (
                    <Box display="flex" alignItems="center">
                        <Typography sx={{ fontSize: 20, fontWeight: 600, color: "neutrals.dark" }}>
                            {intl.formatMessage({ id: "userManagement.setTeam.title" })}
                        </Typography>
                    </Box>
                )
            }
            content={
                <>
                    {isMultipleUsers ? (
                        <Typography
                            sx={{ fontSize: 14, fontWeight: 500, color: "secondary.main" }}
                            variant="subtitle1"
                            mb={2}
                        >
                            {intl.formatMessage({ id: "userManagement.setTeam.description" })}
                        </Typography>
                    ) : (
                        <Typography
                            sx={{ fontSize: 14, fontWeight: 500, color: "secondary.main" }}
                            variant="subtitle1"
                            mb={2}
                        >
                            {intl.formatMessage({ id: "userManagement.setTeam.descriptionSingleUser" })}
                        </Typography>
                    )}

                    <Box mb={2}>
                        <TextField
                            size="small"
                            id="select-team"
                            name="select-team"
                            autoComplete="off"
                            value={filter}
                            onChange={(event) => handleFilter(event)}
                            placeholder="Search or Add Team"
                            inputProps={{
                                maxLength: 60,
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CustomIcon icon={icons.searchInverted} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="start">
                                        {isFiltering && (
                                            <Button
                                                variant="text"
                                                disabled={!isNewTeamName || isNameLikePlaceholder}
                                                onClick={handleAddTeam}
                                                sx={(theme) => ({
                                                    padding: 0,
                                                    fontSize: 12,
                                                    minWidth: "auto",
                                                    letterSpacing: "1.25px",
                                                    textDecoration: "underline",
                                                    color: theme.palette.button.hover1.from,
                                                    "&:hover": {
                                                        textDecoration: "underline",
                                                        color: theme.palette.button.hover1.to,
                                                    },
                                                    "&:active": {
                                                        color: theme.palette.button.selected,
                                                    },
                                                    "&.Mui-disabled": {
                                                        color: theme.palette.text.disabled,
                                                    },
                                                })}
                                            >
                                                {intl.formatMessage({ id: "userManagement.setTeam.label.addTeam" })}
                                            </Button>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: "100%",
                                "& .MuiInputBase-input": {
                                    height: 22,
                                },
                                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                                    fontSize: 16,
                                },
                            }}
                        />
                    </Box>
                    <Box>
                        {filteredList.map((chip) => (
                            <Chip
                                onClick={() => handleSelectTeam(chip)}
                                color="default"
                                key={chip.id}
                                label={chip.name}
                                sx={(theme) => {
                                    const hasSelectedTeam = teams.some((team) => team?.selected);
                                    return {
                                        margin: theme.spacing(0.5),
                                        height: 28,
                                        borderWidth: "1px",
                                        color: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.light,
                                        background: theme.palette.primary.light,
                                        opacity: hasSelectedTeam && !chip?.selected ? 0.5 : 1,
                                        "&:hover, &:focus": {
                                            background: `${theme.palette.neutrals.light} !important`,
                                            boxShadow: "0 2px 7px 0 #00000026",
                                        },
                                        "& .MuiChip-label": {
                                            fontSize: 12,
                                            fontWeight: chip?.selected ? 700 : 400,
                                            color: theme.palette.common.black,
                                            letterSpacing: "0.4px",
                                            textTransform: "capitalize",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: theme.palette.button.active.to,
                                            width: 18,
                                            "&:hover": {
                                                color: theme.palette.button.active.to,
                                            },
                                        },
                                    };
                                }}
                            />
                        ))}
                    </Box>
                </>
            }
            actions={[
                {
                    label: <FormattedMessage id="hubManagement.client.removeUserModal.cancelLabel" />,
                    buttonProps: { variant: "outlined" },
                    onClickAction: () => cancel(),
                },
                {
                    label: <FormattedMessage id="hubManagement.client.removeUserModal.confirmLabel" />,
                    startIcon: <CustomIcon icon={icons.check} sx={{ fontSize: 10, color: "white" }} />,
                    onClickAction: () => confirmAddTeam(),
                    buttonProps: { disabled: isConfirmDisabled || isNameLikePlaceholder },
                },
            ]}
        />
    );
};

export default SelectTeam;
